import type { ChannelMissReason } from '../../../features/_shared/channel-types'
import type { LocaleCode } from '../../../pages/cogo-marketing/i18n/locales'
import { getLocale } from '../../../pages/cogo-marketing/i18n/locale-store'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'

interface MissReasonCopy {
  /** User-facing message (positive framing). */
  title: string
  /** 'transient' suggests retry; 'permanent' suggests different input. */
  tone: 'transient' | 'permanent'
}

const COPY: Record<ChannelMissReason, MissReasonCopy> = {
  'no-candidates': { title: "Couldn't find a matching pattern", tone: 'permanent' },
  'no-template': { title: "That type isn't ready yet", tone: 'permanent' },
  'no-binding-schema': { title: "Can't recognize the data shape", tone: 'permanent' },
  'kg-init-failed': { title: 'Please try again in a moment', tone: 'transient' },
  'bind-throw': { title: 'Something went wrong processing it', tone: 'transient' },
  'wasm-unavailable': { title: "Your browser doesn't support this", tone: 'permanent' },
  'aborted': { title: 'Cancelled', tone: 'transient' },
  'unsupported-mode': { title: 'Unsupported mode', tone: 'permanent' },
  'backend-failed': { title: 'Server connection failed', tone: 'transient' },
}

export function getMissReasonCopy(reason: ChannelMissReason): MissReasonCopy {
  return COPY[reason] ?? { title: 'Something unexpected happened', tone: 'transient' }
}

/**
 * Synthetic non-enum failure reason produced by the channel router when a
 * SHACL validation step rejects a generated page. The producer
 * (`use-channel-dispatch.ts`) and this humanizer must share the SAME literal
 * so they can never drift — the producer imports this constant instead of
 * hard-coding `'shaclFail:'`.
 *
 * Format: `shaclFail:<pageId>` (e.g. `shaclFail:home`).
 */
export const SHACL_FAIL_PREFIX = 'shaclFail:'

/**
 * A local edit was understood, but applying it without an exact element would
 * be unsafe. Keep this literal shared between the producers and recovery UI so
 * creators never see the internal blocker code.
 */
export const AMBIGUOUS_TARGET_REASON = 'ambiguous-target'

/**
 * The chat request reached the local planner but the planner has no mapping
 * for it, and a DOCUMENT SOURCE is open in this session.
 *
 * Measured 2026-07-30: an imported document is not an input to generation.
 * `@cogo/source-document` parses it in the browser for reading and search, but
 * nothing persists the extraction and no generation service reads it — the
 * `cogo.source-ingest-bundle.v1` hand-off has zero callers. A build started
 * from here is therefore built from the user's WORDS ONLY; the document
 * contributes nothing. Before this reason existed the product accepted the
 * request and produced an unrelated app while the cited document sat on screen.
 * The copy below says that plainly so the choice is informed, not silent.
 */
export const DOCUMENT_NOT_GENERATION_SOURCE_REASON =
  'document-not-generation-source'

/**
 * The chat request reached the local planner but the planner has no mapping
 * for it, and NO document source is open. The recovery action re-sends the
 * request through the build planner, which is a genuinely different path from
 * the local edit planner that just declined it.
 *
 * Replaces the raw `knowledge-gap` blocker code, which used to be rendered
 * verbatim as the bubble title alongside an internal-vocabulary detail line.
 */
export const LOCAL_EDIT_PLAN_UNAVAILABLE_REASON = 'local-edit-plan-unavailable'

type ChatRequestBlockerKind =
  | 'document-not-generation-source'
  | 'local-edit-plan-unavailable'

/**
 * Creator-facing copy for the two chat-request blockers above, in every shipped
 * locale. Shape mirrors `APP_GENERATION_FAILURE_COPY`: `detail` is rendered to
 * creators (not just developers), so it carries the "what you CAN do" half.
 */
const CHAT_REQUEST_BLOCKER_COPY: Readonly<
  Record<LocaleCode, Record<ChatRequestBlockerKind, HumanizedFailure>>
> = {
  en: {
    'document-not-generation-source': {
      title: "Coogo can't build from your document yet",
      detail:
        'The document you opened can be read and searched here, but it is not used when an app is built. Try again to build from your description alone — nothing in the result will come from the document.',
      retryable: true,
    },
    'local-edit-plan-unavailable': {
      title: "That isn't something I can change directly here",
      detail:
        'Try again and Coogo will plan it as a new build instead, or select the exact part of the app you want to change.',
      retryable: true,
    },
  },
  ko: {
    'document-not-generation-source': {
      title: '아직 문서를 근거로 앱을 만들지는 못합니다',
      detail:
        '연 문서는 여기서 읽고 찾아볼 수 있지만, 앱을 만들 때는 사용되지 않습니다. 다시 시도하면 문서가 아니라 입력하신 설명만으로 앱을 만듭니다.',
      retryable: true,
    },
    'local-edit-plan-unavailable': {
      title: '여기서 바로 바꿀 수 있는 요청이 아닙니다',
      detail:
        '다시 시도하면 새로 만드는 요청으로 처리합니다. 또는 바꾸고 싶은 부분을 앱에서 직접 선택해 주세요.',
      retryable: true,
    },
  },
  ja: {
    'document-not-generation-source': {
      title: 'ドキュメントを根拠にアプリを作ることはまだできません',
      detail:
        '開いたドキュメントはここで読んだり検索したりできますが、アプリの生成には使われません。再試行すると、ドキュメントではなく入力した説明だけでアプリを作ります。',
      retryable: true,
    },
    'local-edit-plan-unavailable': {
      title: 'ここで直接変更できる依頼ではありません',
      detail:
        '再試行すると新規作成の依頼として扱います。または変更したい部分をアプリ上で選択してください。',
      retryable: true,
    },
  },
  zh: {
    'document-not-generation-source': {
      title: '暂时还不能以文档为依据生成应用',
      detail:
        '打开的文档可以在这里阅读和搜索，但生成应用时不会使用它。重试将只根据你的描述生成，结果不会来自该文档。',
      retryable: true,
    },
    'local-edit-plan-unavailable': {
      title: '这不是可以在这里直接修改的请求',
      detail: '重试将按新建应用的请求处理，或在应用中选择你想修改的具体部分。',
      retryable: true,
    },
  },
  'zh-TW': {
    'document-not-generation-source': {
      title: '目前還無法以文件為依據產生應用',
      detail:
        '開啟的文件可以在這裡閱讀和搜尋，但產生應用時不會使用它。重試將只依你的描述產生，結果不會來自該文件。',
      retryable: true,
    },
    'local-edit-plan-unavailable': {
      title: '這不是可以在這裡直接修改的請求',
      detail: '重試將以新建應用的請求處理，或在應用中選擇你想修改的具體部分。',
      retryable: true,
    },
  },
}

/**
 * Humanize a chat-request blocker reason, or `null` when `reason` is not one.
 *
 * Returning `null` (rather than a fallback) keeps this additive: every other
 * failure reason continues through `humanizeFailureReason` unchanged.
 */
export function humanizeChatRequestBlocker(
  reason: string,
  locale: LocaleCode = 'en',
): HumanizedFailure | null {
  if (
    reason !== DOCUMENT_NOT_GENERATION_SOURCE_REASON &&
    reason !== LOCAL_EDIT_PLAN_UNAVAILABLE_REASON
  ) {
    return null
  }
  return CHAT_REQUEST_BLOCKER_COPY[locale][reason]
}

type AppGenerationFailureKind =
  | 'block-quality'
  | 'independent-review'
  | 'layout'
  | 'blocks'
  | 'theme'
  | 'assembly'
  | 'persist'
  | 'spec'
  | 'timeout'
  | 'cancelled'
  | 'workflow'
  | 'source-bound-unavailable'

/**
 * Reason code carried by `StudioSourceBoundGenerationUnavailableError`
 * (`features/chat/services/source-design-system-generation-bridge.ts`).
 *
 * Duplicated as a literal here ON PURPOSE: this module is imported by the chat
 * failure renderer, and importing the bridge would drag the whole OpenPencil
 * admission graph into that bundle. The bridge owns the canonical constant and
 * `miss-reason-copy.test.ts` asserts the two literals stay identical, so they
 * cannot silently drift.
 */
const SOURCE_BOUND_GENERATION_UNAVAILABLE_REASON =
  'studio-source-bound-generation-unavailable'

const APP_GENERATION_FAILURE_KIND: Readonly<Record<string, AppGenerationFailureKind>> = {
  'app-generation-block-quality-blocked': 'block-quality',
  'app-generation-quality-review-blocked': 'independent-review',
  'app-generation-layout-failed': 'layout',
  'app-generation-blocks-failed': 'blocks',
  'app-generation-theme-failed': 'theme',
  'app-generation-assembly-failed': 'assembly',
  'app-generation-persist-failed': 'persist',
  'app-generation-spec-invalid': 'spec',
  'app-generation-workflow-failed': 'workflow',
  'app-generation-job-workflow-failed': 'workflow',
  'app-generation-job-terminal-timeout': 'timeout',
  APP_GENERATION_JOB_TERMINAL_SLO_EXCEEDED: 'timeout',
  APP_GENERATION_JOB_CANCELLED: 'cancelled',
  [SOURCE_BOUND_GENERATION_UNAVAILABLE_REASON]: 'source-bound-unavailable',
}

type AppGenerationFailureCopy = Record<AppGenerationFailureKind, HumanizedFailure>

const APP_GENERATION_FAILURE_COPY: Readonly<Record<LocaleCode, AppGenerationFailureCopy>> = {
  en: {
    'block-quality': {
      title: "The app didn't meet Coogo's quality checks",
      detail: 'Try again, or simplify the workflow you asked for.',
      retryable: true,
    },
    'independent-review': {
      title: "The app didn't pass the independent quality review",
      detail: 'Clarify the most important screens and actions, then try again.',
      retryable: true,
    },
    layout: {
      title: "Coogo couldn't complete the page layout",
      detail: 'Try again with fewer screens at once.',
      retryable: true,
    },
    blocks: {
      title: "Coogo couldn't complete the app sections",
      detail: 'Try again, or simplify the requested workflow.',
      retryable: true,
    },
    theme: {
      title: "Coogo couldn't complete the app theme",
      detail: 'Try again, or describe the visual direction more simply.',
      retryable: true,
    },
    assembly: {
      title: "Coogo couldn't assemble the app safely",
      detail: 'Try again. Your existing project has not been changed.',
      retryable: true,
    },
    persist: {
      title: "The app was created but couldn't be saved safely",
      detail: 'Try again. No incomplete app was applied.',
      retryable: true,
    },
    spec: {
      title: 'The app request needs a little more detail',
      detail: 'Describe the main users, screens, and actions, then start again.',
      retryable: false,
    },
    timeout: {
      title: 'App generation took too long',
      detail: 'Try again with fewer screens or a simpler workflow.',
      retryable: true,
    },
    cancelled: {
      title: 'App generation was cancelled',
      detail: "Start again when you're ready.",
      retryable: true,
    },
    workflow: {
      title: "Coogo couldn't complete this build",
      detail: 'Try again. Your existing project has not been changed.',
      retryable: true,
    },
    // Not a run that went wrong — a capability that does not exist yet. Retry
    // would fail identically, so `retryable: false` makes the recovery button
    // read "Start over" and the detail names the path that DOES work today.
    'source-bound-unavailable': {
      title: "Coogo can't build an app from an imported design source yet",
      detail:
        'Start a new project without importing a source. This project keeps its imported source for review.',
      retryable: false,
    },
  },
  ko: {
    'block-quality': {
      title: '생성된 앱이 Coogo 품질 검사를 통과하지 못했습니다',
      detail: '다시 시도하거나 요청한 작업 흐름을 더 단순하게 설명해 주세요.',
      retryable: true,
    },
    'independent-review': {
      title: '독립 품질 검토를 통과하지 못했습니다',
      detail: '핵심 화면과 동작을 더 명확히 한 뒤 다시 시도해 주세요.',
      retryable: true,
    },
    layout: {
      title: '페이지 구성을 완성하지 못했습니다',
      detail: '한 번에 요청하는 화면 수를 줄여 다시 시도해 주세요.',
      retryable: true,
    },
    blocks: {
      title: '앱의 구성 요소를 완성하지 못했습니다',
      detail: '다시 시도하거나 요청한 작업 흐름을 단순하게 설명해 주세요.',
      retryable: true,
    },
    theme: {
      title: '앱 테마를 완성하지 못했습니다',
      detail: '다시 시도하거나 원하는 시각 방향을 더 단순하게 설명해 주세요.',
      retryable: true,
    },
    assembly: {
      title: '앱을 안전하게 조립하지 못했습니다',
      detail: '다시 시도해 주세요. 기존 프로젝트는 변경되지 않았습니다.',
      retryable: true,
    },
    persist: {
      title: '앱을 만들었지만 안전하게 저장하지 못했습니다',
      detail: '다시 시도해 주세요. 미완성 앱은 적용되지 않았습니다.',
      retryable: true,
    },
    spec: {
      title: '앱 요청에 정보가 조금 더 필요합니다',
      detail: '주요 사용자, 화면, 동작을 설명한 뒤 새로 시작해 주세요.',
      retryable: false,
    },
    timeout: {
      title: '앱 생성 시간이 너무 길어 중단되었습니다',
      detail: '화면 수나 작업 흐름을 줄여 다시 시도해 주세요.',
      retryable: true,
    },
    cancelled: {
      title: '앱 생성이 취소되었습니다',
      detail: '준비되면 다시 시작해 주세요.',
      retryable: true,
    },
    workflow: {
      title: '이번 앱 생성을 완료하지 못했습니다',
      detail: '다시 시도해 주세요. 기존 프로젝트는 변경되지 않았습니다.',
      retryable: true,
    },
    'source-bound-unavailable': {
      title: '가져온 디자인 소스로 앱을 만드는 기능은 아직 제공되지 않습니다',
      detail:
        '소스를 가져오지 않은 새 프로젝트에서 만들어 주세요. 이 프로젝트의 가져온 소스는 그대로 보관됩니다.',
      retryable: false,
    },
  },
  ja: {
    'block-quality': {
      title: '生成したアプリがCoogoの品質チェックを通過しませんでした',
      detail: 'もう一度試すか、依頼したワークフローを簡潔にしてください。',
      retryable: true,
    },
    'independent-review': {
      title: '独立した品質レビューを通過しませんでした',
      detail: '重要な画面と操作を明確にして、もう一度試してください。',
      retryable: true,
    },
    layout: {
      title: 'ページレイアウトを完成できませんでした',
      detail: '一度に依頼する画面を減らして、もう一度試してください。',
      retryable: true,
    },
    blocks: {
      title: 'アプリのセクションを完成できませんでした',
      detail: 'もう一度試すか、依頼したワークフローを簡潔にしてください。',
      retryable: true,
    },
    theme: {
      title: 'アプリのテーマを完成できませんでした',
      detail: 'もう一度試すか、希望する見た目を簡潔に説明してください。',
      retryable: true,
    },
    assembly: {
      title: 'アプリを安全に組み立てられませんでした',
      detail: 'もう一度試してください。既存のプロジェクトは変更されていません。',
      retryable: true,
    },
    persist: {
      title: 'アプリは生成されましたが安全に保存できませんでした',
      detail: 'もう一度試してください。未完成のアプリは適用されていません。',
      retryable: true,
    },
    spec: {
      title: 'アプリの依頼にもう少し情報が必要です',
      detail: '主な利用者、画面、操作を説明してからやり直してください。',
      retryable: false,
    },
    timeout: {
      title: 'アプリ生成に時間がかかりすぎました',
      detail: '画面数やワークフローを減らして、もう一度試してください。',
      retryable: true,
    },
    cancelled: {
      title: 'アプリ生成がキャンセルされました',
      detail: '準備ができたらもう一度開始してください。',
      retryable: true,
    },
    workflow: {
      title: '今回のアプリ生成を完了できませんでした',
      detail: 'もう一度試してください。既存のプロジェクトは変更されていません。',
      retryable: true,
    },
    'source-bound-unavailable': {
      title: '取り込んだデザインソースからアプリを生成する機能はまだ利用できません',
      detail:
        'ソースを取り込んでいない新しいプロジェクトで作成してください。このプロジェクトの取り込んだソースはそのまま保持されます。',
      retryable: false,
    },
  },
  zh: {
    'block-quality': {
      title: '生成的应用未通过 Coogo 质量检查',
      detail: '请重试，或简化所需的工作流程。',
      retryable: true,
    },
    'independent-review': {
      title: '应用未通过独立质量审核',
      detail: '请明确最重要的页面和操作，然后重试。',
      retryable: true,
    },
    layout: {
      title: '无法完成页面布局',
      detail: '请减少一次生成的页面数量，然后重试。',
      retryable: true,
    },
    blocks: {
      title: '无法完成应用的各个部分',
      detail: '请重试，或简化所需的工作流程。',
      retryable: true,
    },
    theme: {
      title: '无法完成应用主题',
      detail: '请重试，或更简洁地描述视觉方向。',
      retryable: true,
    },
    assembly: {
      title: '无法安全组装应用',
      detail: '请重试。现有项目未被更改。',
      retryable: true,
    },
    persist: {
      title: '应用已生成，但无法安全保存',
      detail: '请重试。未完成的应用不会被应用。',
      retryable: true,
    },
    spec: {
      title: '应用需求还需要一些信息',
      detail: '请描述主要用户、页面和操作，然后重新开始。',
      retryable: false,
    },
    timeout: {
      title: '应用生成耗时过长',
      detail: '请减少页面数量或简化工作流程后重试。',
      retryable: true,
    },
    cancelled: {
      title: '应用生成已取消',
      detail: '准备好后可以重新开始。',
      retryable: true,
    },
    workflow: {
      title: '无法完成本次应用生成',
      detail: '请重试。现有项目未被更改。',
      retryable: true,
    },
    'source-bound-unavailable': {
      title: '暂不支持从导入的设计源生成应用',
      detail: '请在未导入源的新项目中创建。本项目导入的源会继续保留以供查看。',
      retryable: false,
    },
  },
  'zh-TW': {
    'block-quality': {
      title: '產生的應用未通過 Coogo 品質檢查',
      detail: '請重試，或簡化所需的工作流程。',
      retryable: true,
    },
    'independent-review': {
      title: '應用未通過獨立品質審查',
      detail: '請明確最重要的頁面和操作，然後重試。',
      retryable: true,
    },
    layout: {
      title: '無法完成頁面版面配置',
      detail: '請減少一次產生的頁面數量，然後重試。',
      retryable: true,
    },
    blocks: {
      title: '無法完成應用的各個部分',
      detail: '請重試，或簡化所需的工作流程。',
      retryable: true,
    },
    theme: {
      title: '無法完成應用主題',
      detail: '請重試，或更簡潔地描述視覺方向。',
      retryable: true,
    },
    assembly: {
      title: '無法安全組裝應用',
      detail: '請重試。現有專案未被變更。',
      retryable: true,
    },
    persist: {
      title: '應用已產生，但無法安全儲存',
      detail: '請重試。未完成的應用不會被套用。',
      retryable: true,
    },
    spec: {
      title: '應用需求還需要一些資訊',
      detail: '請描述主要使用者、頁面和操作，然後重新開始。',
      retryable: false,
    },
    timeout: {
      title: '應用產生耗時過長',
      detail: '請減少頁面數量或簡化工作流程後重試。',
      retryable: true,
    },
    cancelled: {
      title: '應用產生已取消',
      detail: '準備好後可以重新開始。',
      retryable: true,
    },
    workflow: {
      title: '無法完成本次應用產生',
      detail: '請重試。現有專案未被變更。',
      retryable: true,
    },
    'source-bound-unavailable': {
      title: '暫不支援從匯入的設計來源產生應用',
      detail: '請在未匯入來源的新專案中建立。本專案匯入的來源會繼續保留以供檢視。',
      retryable: false,
    },
  },
}

const UNKNOWN_APP_GENERATION_FAILURE_TITLE: Readonly<Record<LocaleCode, string>> = {
  en: "Couldn't build your app — please try again",
  ko: '앱을 만들지 못했습니다. 다시 시도해 주세요',
  ja: 'アプリを生成できませんでした。もう一度試してください',
  zh: '无法生成应用，请重试',
  'zh-TW': '無法產生應用，請重試',
}

/**
 * The result of humanizing ANY failure-reason string the failure surfaces
 * receive — chat `ai-result-failure` bubble, the generation status pill, and
 * (optionally) the build-mode canvas.
 */
export interface HumanizedFailure {
  /** Creator-friendly headline (no engineering jargon, no emoji). */
  title: string
  /** Optional secondary line (e.g. the failing page id for a SHACL reject). */
  detail?: string
  /** `true` → offer "Try again"; `false` → offer "Start over". */
  retryable: boolean
}

/**
 * Creator-only humanizer for the server's bounded app-generation codes.
 * Only exact allowlisted codes receive specific copy. Unknown values and raw
 * provider prose deliberately collapse to a generic localized message.
 */
export function humanizeAppGenerationFailureReason(
  reason: string,
  locale: LocaleCode = 'en',
): HumanizedFailure {
  const kind = APP_GENERATION_FAILURE_KIND[reason]
  if (!kind) {
    return {
      title: UNKNOWN_APP_GENERATION_FAILURE_TITLE[locale],
      retryable: true,
    }
  }
  return APP_GENERATION_FAILURE_COPY[locale][kind]
}

/** Set of the 9 known ChannelMissReason codes (the COPY keys). */
const KNOWN_REASON_CODES = new Set<string>(Object.keys(COPY))

/**
 * Tolerant failure-reason humanizer shared by every failure surface.
 *
 * The `reason` carried on `ai-result-failure` / the dispatch-status `message`
 * is a LOOSE string (not typed to `ChannelMissReason`): it may be one of the 9
 * known codes, a synthetic `shaclFail:<pageId>` value, OR free-form prose that
 * has ALREADY been humanized by its producer (e.g. register-studio-tools'
 * `'App generation failed: <err>'` and the finally-path
 * `'Generation ended without a render.'`). A blind `getMissReasonCopy(...)`
 * would mangle that prose into the generic fallback, so the branch order
 * below MATTERS:
 *
 *   1. KNOWN code  → use the curated COPY (tone drives `retryable`).
 *   2. shaclFail:… → humanized SHACL copy, page id surfaced as `detail`.
 *   3. free-form   → PASS THROUGH verbatim as the title (MANDATORY — without
 *                    this branch already-humanized producer prose regresses).
 *
 * `retryable` maps tone: transient → true, permanent → false; synthetic and
 * pass-through reasons are treated as transient (a retry is worth offering).
 */
export function humanizeFailureReason(
  reason: string,
  locale: LocaleCode = getLocale(),
): HumanizedFailure {
  // F8 — this took NO locale, unlike `humanizeChatRequestBlocker` and
  // `humanizeAppGenerationFailureReason` in this same file, which both do. Its
  // English title and detail therefore landed unchanged inside a Korean card
  // whose sibling button came from the localized dictionary — one card, two
  // languages. The `tone`/`retryable` decision is behaviour and still comes
  // from COPY; only the words come from the locale.
  const chrome = COOGO_IDE_CHROME_COPY[locale] ?? COOGO_IDE_CHROME_COPY.en

  // (1) Known ChannelMissReason code → curated copy.
  if (KNOWN_REASON_CODES.has(reason)) {
    const copy = COPY[reason as ChannelMissReason]
    return {
      title: chrome.missReason[reason as ChannelMissReason],
      retryable: copy.tone === 'transient',
    }
  }

  // (2) Synthetic SHACL-failure reason → humanized copy, page id as detail.
  if (reason.startsWith(SHACL_FAIL_PREFIX)) {
    const pageId = reason.slice(SHACL_FAIL_PREFIX.length).trim()
    return {
      title: chrome.emptyState.pageDidNotPassChecks,
      detail: pageId.length > 0 && pageId !== 'unknown' ? pageId : undefined,
      retryable: true,
    }
  }

  if (reason === AMBIGUOUS_TARGET_REASON) {
    return {
      title: chrome.emptyState.selectExactPart,
      detail: chrome.failure.selectExactPartDetail,
      retryable: true,
    }
  }

  // (3) Free-form prose (already humanized by its producer) → pass through.
  // Deliberately NOT localized: this branch carries the producer's own already
  // creator-facing sentence, and translating it here would mean guessing.
  return { title: reason, retryable: true }
}
